import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const r = await api.get("/auth/me");
      setUser(r.data);
    } catch (error) {
      console.error("Failed to refresh user session:", error);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // If OAuth callback in URL hash, skip /me; AuthCallback will run
    if (typeof window !== "undefined" && window.location.hash && window.location.hash.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const loginWithPassword = async (email, password) => {
    const r = await api.post("/auth/login", { email, password });
    // Store token in localStorage (Note: httpOnly cookies are more secure but require backend changes)
    try {
      localStorage.setItem("iv_token", r.data.session_token);
    } catch (error) {
      console.warn("Failed to store token:", error);
    }
    setUser(r.data.user);
    return r.data.user;
  };

  const registerWithPassword = async ({ email, password, name, role }) => {
    const r = await api.post("/auth/register", { email, password, name, role });
    try {
      localStorage.setItem("iv_token", r.data.session_token);
    } catch (error) {
      console.warn("Failed to store token:", error);
    }
    setUser(r.data.user);
    return r.data.user;
  };

  const exchangeGoogleSession = async (session_id) => {
    const r = await api.post("/auth/google/session", { session_id });
    try {
      localStorage.setItem("iv_token", r.data.session_token);
    } catch (error) {
      console.warn("Failed to store token:", error);
    }
    setUser(r.data.user);
    return r.data.user;
  };

  const logout = async () => {
    try { 
      await api.post("/auth/logout"); 
    } catch (error) {
      console.error("Logout request failed:", error);
    }
    try {
      localStorage.removeItem("iv_token");
    } catch (error) {
      console.warn("Failed to remove token:", error);
    }
    setUser(null);
  };

  const updateRole = async (role) => {
    const r = await api.post("/auth/role", { role });
    setUser(r.data);
    return r.data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, refresh, loginWithPassword, registerWithPassword, exchangeGoogleSession, logout, updateRole, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
